/**
 * SystemHealthRegistry
 *
 * Tracks the live health of all registered game systems. Each system can
 * report its own status, or the registry can detect stale/frozen systems
 * by monitoring how long ago they last "ticked".
 *
 * Usage:
 *   SystemHealthRegistry.getInstance().reportTick('PhysicsSystem');
 *   SystemHealthRegistry.getInstance().reportError('MapSystem', 'Failed to load level');
 */

export type SystemHealthStatus = 'healthy' | 'degraded' | 'error' | 'unknown';

export interface SystemHealthEntry {
  id: string;
  status: SystemHealthStatus;
  lastTickMs: number;       // Date.now() of last tick
  errorCount: number;
  lastError?: string;
  lastErrorStack?: string;
  metadata?: Record<string, any>;
}

const STALE_THRESHOLD_MS = 2000; // >2s without a tick = degraded

export class SystemHealthRegistry {
  private static instance: SystemHealthRegistry;
  private entries: Map<string, SystemHealthEntry> = new Map();

  static getInstance(): SystemHealthRegistry {
    if (!this.instance) {
      this.instance = new SystemHealthRegistry();
    }
    return this.instance;
  }

  /** Register a system (called once at system init). */
  register(id: string): void {
    if (!this.entries.has(id)) {
      this.entries.set(id, {
        id,
        status: 'unknown',
        lastTickMs: Date.now(),
        errorCount: 0,
      });
    }
  }

  /** Report a successful tick (called each update() frame). */
  reportTick(id: string, metadata?: Record<string, any>): void {
    const entry = this.entries.get(id);
    if (!entry) {
      this.register(id);
    }
    const e = this.entries.get(id)!;
    e.lastTickMs = Date.now();
    e.status = 'healthy';
    if (metadata) e.metadata = { ...e.metadata, ...metadata };
  }

  /** Report an error from a system. */
  reportError(id: string, message: string, stack?: string): void {
    const entry = this.entries.get(id);
    if (!entry) this.register(id);
    const e = this.entries.get(id)!;
    e.status = 'error';
    e.errorCount++;
    e.lastError = message;
    e.lastErrorStack = stack;
  }

  /** Mark a system as explicitly healthy. */
  reportHealthy(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) this.register(id);
    const e = this.entries.get(id)!;
    e.status = 'healthy';
    e.lastTickMs = Date.now();
  }

  /**
   * Get all health entries with staleness evaluation applied.
   * Systems that have not ticked within STALE_THRESHOLD_MS are flagged as 'degraded'.
   */
  getAll(): SystemHealthEntry[] {
    const now = Date.now();
    return Array.from(this.entries.values()).map(entry => {
      // Don't downgrade systems already in 'error' state
      if (entry.status === 'error') return { ...entry };
      const msSinceTick = now - entry.lastTickMs;
      if (msSinceTick > STALE_THRESHOLD_MS) {
        return { ...entry, status: 'degraded' as SystemHealthStatus };
      }
      return { ...entry };
    });
  }

  /** Get a single entry. */
  get(id: string): SystemHealthEntry | undefined {
    return this.entries.get(id);
  }

  /** Clear all entries (for scene restarts). */
  clear(): void {
    this.entries.clear();
  }

  /** Summary counts. */
  getSummary(): { healthy: number; degraded: number; error: number; unknown: number; total: number } {
    const all = this.getAll();
    return {
      healthy: all.filter(e => e.status === 'healthy').length,
      degraded: all.filter(e => e.status === 'degraded').length,
      error: all.filter(e => e.status === 'error').length,
      unknown: all.filter(e => e.status === 'unknown').length,
      total: all.length,
    };
  }
}
